<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppImage from '@uiKit/elements/Image.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import PropertyDetailSection from './PropertyDetailSection.vue'
import { iconForKeyword, isIconUrl } from '../utils/propertyIconUtils'
import type { AmenityItem } from '../utils/propertyDetailUtils'

// AMENITIES (Figma 32590:61812) — a spread row of icon tiles with labels.
// Up to six fit the row; beyond that the row becomes a carousel that drifts
// sideways on its own (looping seamlessly over a doubled list), pauses while
// hovered / touched / focused, and can still be swiped by hand.
// Uploaded icon URLs render as images; keywords map to kit icons, and a URL
// that fails to load falls back to the keyword icon.

interface Props {
  amenities: AmenityItem[]
}

const props = defineProps<Props>()

const CAROUSEL_THRESHOLD = 6
// Drift speed in px per animation frame (~30px/s at 60fps) — slow enough to read.
const DRIFT_PX_PER_FRAME = 0.5

const isCarousel = computed(() => props.amenities.length > CAROUSEL_THRESHOLD)
// Doubled list so the scroll can wrap from the halfway point without a jump.
const rendered = computed(() =>
  isCarousel.value
    ? [...props.amenities, ...props.amenities].map((amenity, index) => ({
        amenity,
        key: `${amenity.label}-${index}`,
        isClone: index >= props.amenities.length,
      }))
    : props.amenities.map((amenity) => ({ amenity, key: amenity.label, isClone: false })),
)

const failed = ref<Set<string>>(new Set())
watch(
  () => props.amenities.map((amenity) => amenity.label).join('|'),
  () => {
    failed.value = new Set()
  },
)
const markFailed = (label: string) => {
  failed.value = new Set(failed.value).add(label)
}
const showImage = (amenity: AmenityItem) =>
  isIconUrl(amenity.icon) && !failed.value.has(amenity.label)

// ── Auto-scroll ─────────────────────────────────────────────────────────
const track = ref<InstanceType<typeof Box> | null>(null)
const isPaused = ref(false)
let frame = 0
// scrollLeft rounds to whole pixels, so sub-pixel drift is accumulated here.
let position = 0

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const tick = () => {
  const element = track.value?.$el as HTMLElement | undefined
  if (element && !isPaused.value) {
    // Where the doubled copy begins — scrolling past it wraps back invisibly.
    const firstClone = element.children[props.amenities.length] as HTMLElement | undefined
    const first = element.children[0] as HTMLElement | undefined
    const half =
      firstClone && first ? firstClone.offsetLeft - first.offsetLeft : element.scrollWidth / 2
    // Pick up any manual swipe before drifting on from there.
    if (Math.abs(element.scrollLeft - position) > 1) position = element.scrollLeft
    position += DRIFT_PX_PER_FRAME
    if (position >= half) position -= half
    element.scrollLeft = position
  }
  frame = requestAnimationFrame(tick)
}

const start = () => {
  cancelAnimationFrame(frame)
  if (isCarousel.value && !prefersReducedMotion()) frame = requestAnimationFrame(tick)
}

onMounted(start)
watch(isCarousel, start)
onBeforeUnmount(() => cancelAnimationFrame(frame))

const pause = () => {
  isPaused.value = true
}
const resume = () => {
  isPaused.value = false
}
</script>

<template>
  <PropertyDetailSection v-if="amenities.length" title="Amenities">
    <Box
      ref="track"
      is="ul"
      class="pd-amenities"
      :class="{ 'pd-amenities--carousel': isCarousel }"
      :aria-label="isCarousel ? 'Amenities, scrolling list' : undefined"
      @mouseenter="pause"
      @mouseleave="resume"
      @touchstart.passive="pause"
      @touchend="resume"
      @focusin="pause"
      @focusout="resume"
    >
      <Box
        v-for="item in rendered"
        :key="item.key"
        is="li"
        direction="col"
        align="center"
        gap="6px"
        class="pd-amenities__item"
        :aria-hidden="item.isClone || undefined"
      >
        <Box align="center" justify="center" class="pd-amenities__tile">
          <AppImage
            v-if="showImage(item.amenity)"
            :src="item.amenity.icon"
            alt=""
            class="pd-amenities__img"
            @error="markFailed(item.amenity.label)"
          />
          <PiIcon
            v-else
            :name="iconForKeyword(`${item.amenity.icon} ${item.amenity.label}`)"
            class="pd-amenities__icon"
          />
        </Box>
        <Text
          type="body"
          weight="medium"
          color="muted"
          align="center"
          is="span"
          class="pd-amenities__label"
        >
          {{ item.amenity.label }}
        </Text>
      </Box>
    </Box>
  </PropertyDetailSection>
</template>

<style scoped>
.pd-amenities {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* One row that scrolls sideways; the scrollbar stays hidden. */
.pd-amenities--carousel {
  flex-wrap: nowrap;
  justify-content: flex-start;
  gap: 32px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  mask-image: linear-gradient(
    to right,
    transparent 0,
    rgb(0 0 0) 24px,
    rgb(0 0 0) calc(100% - 24px),
    transparent 100%
  );
}

.pd-amenities--carousel::-webkit-scrollbar {
  display: none;
}

.pd-amenities--carousel .pd-amenities__item {
  flex: 0 0 auto;
  min-width: 96px;
}

.pd-amenities--carousel .pd-amenities__label {
  white-space: nowrap;
}

.pd-amenities__tile {
  width: 64px;
  height: 64px;
  border: 1px solid var(--p-sliceal-gray-100);
  border-radius: 10px;
  background: var(--p-surface-0);
}

.pd-amenities__img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.pd-amenities__icon {
  font-size: 26px;
  color: var(--p-primary-600);
}
</style>
